/**
 * Add Source Modal Component
 * Handles source registration forms for all source types
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  registerDatabaseSource,
  registerLocalFolderSource,
  registerSharePointSource,
  registerGDriveSource,
} from '../../api/sources';
import { uploadArtifact } from '../../api/artifacts';
import { 
  Database, 
  Plus, 
  X,
  Folder,
  Cloud,
  HardDrive,
  File,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { LocalFolderForm } from './LocalFolderForm';
import { SharePointForm } from './SharePointForm';
import { GDriveForm } from './GDriveForm';

interface AddSourceModalProps {
  projectId: string;
  sourceType: 'database' | 'local_folder' | 'sharepoint' | 'gdrive';
  onSourceTypeChange: (type: 'database' | 'local_folder' | 'sharepoint' | 'gdrive') => void;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSourceModal({ projectId, sourceType, onSourceTypeChange, onClose, onSuccess }: AddSourceModalProps) {
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Register New Source</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Source Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => onSourceTypeChange('database')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'database'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Database className="h-6 w-6 mb-2 text-blue-600" />
                  <div className="font-semibold">Database</div>
                  <div className="text-xs text-gray-500">PostgreSQL, MySQL, MS SQL</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('local_folder')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'local_folder'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Folder className="h-6 w-6 mb-2 text-green-600" />
                  <div className="font-semibold">Local Folder</div>
                  <div className="text-xs text-gray-500">File system directory</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('sharepoint')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'sharepoint'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Cloud className="h-6 w-6 mb-2 text-purple-600" />
                  <div className="font-semibold">SharePoint</div>
                  <div className="text-xs text-gray-500">Microsoft SharePoint</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('gdrive')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'gdrive'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Cloud className="h-6 w-6 mb-2 text-red-600" />
                  <div className="font-semibold">Google Drive</div>
                  <div className="text-xs text-gray-500">Google Drive</div>
                </button>
              </div>
            </div>
            
            {/* Form Content */}
            {sourceType === 'local_folder' && (
              <LocalFolderForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'sharepoint' && (
              <SharePointForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'gdrive' && (
              <GDriveForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'database' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  Database registration requires detailed connection configuration. Please use the API directly or a notebook script. See API docs for schema.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

